import { z } from 'zod';
import { isoDateTime, slugSchema } from './common';

/**
 * URL field that's optional and coerces empty strings (the value the HTML
 * `<input type="url">` emits when blank) to null, so the form doesn't fail
 * the `.url()` validation when the field is left empty.
 */
const optionalUrl = () =>
  z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
    z.string().url().nullable().default(null),
  );

/**
 * Scientific events — upcoming congresses, symposia, workshops and webinars
 * that the MTC community should know about. Separate collection from
 * `formations` because these are NOT MTC's own offerings; they're 3rd-party
 * events curated for the dental community.
 */

export const evenementCategorySchema = z.enum([
  'congres',
  'symposium',
  'workshop',
  'webinaire',
  'autre',
]);
export type EvenementCategory = z.infer<typeof evenementCategorySchema>;

export const evenementStatusSchema = z.enum(['draft', 'published', 'archived']);
export type EvenementStatus = z.infer<typeof evenementStatusSchema>;

/**
 * How registration is handled for an event:
 * - `external-link`: visitor is sent to the organizer's own form/page
 *   (current default behaviour — registrationUrl drives the CTA).
 * - `hosted-mtc`: MTC collects the inscription + payment on its own site.
 *   Activates the `hostedRegistration` block + new public + admin flows.
 */
export const evenementRegistrationModeSchema = z.enum(['external-link', 'hosted-mtc']);
export type EvenementRegistrationMode = z.infer<typeof evenementRegistrationModeSchema>;

/** A scheduling group for ateliers — ateliers that share a timeSlotId are
 *  mutually exclusive on the public inscription form (radio per slot). */
export const evenementTimeSlotSchema = z.object({
  id: z.string(),
  label: z.string().min(1).max(120),
});
export type EvenementTimeSlot = z.infer<typeof evenementTimeSlotSchema>;

/**
 * Per-event speaker registry. Created once by the admin, then referenced from
 * conferences / ateliers / masterclasses by `conferencierIds`. Avoids the
 * duplication of typing names everywhere and powers the modal popover on the
 * public site (photo + bio + CV link).
 */
export const evenementConferencierSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(160),
  /** Discipline / specialty / title (e.g. "Implantologie · Paris VII"). */
  title: z.string().max(200).optional(),
  photoUrl: z.string().url().nullable().default(null),
  /** Free-text biography (markdown light supported by the renderer). */
  bio: z.string().max(5000).optional(),
  /** Optional uploaded CV PDF. */
  cvPdfUrl: z.string().url().nullable().default(null),
});
export type EvenementConferencier = z.infer<typeof evenementConferencierSchema>;

/** A scientific lecture/conférence. Included in the general inscription —
 *  NOT separately selectable, NOT priced. Shown in the "Programme" section. */
export const evenementConferenceSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  startDateTime: isoDateTime,
  endDateTime: isoDateTime,
  conferencierIds: z.array(z.string()).default([]),
  afficheUrl: z.string().url().nullable().default(null),
});
export type EvenementConference = z.infer<typeof evenementConferenceSchema>;

/** One workshop / atelier within a hosted event. */
export const evenementAtelierSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  /** Empty / null → standalone (no exclusivity with anything else). */
  timeSlotId: z.string().nullable().default(null),
  startDateTime: isoDateTime,
  endDateTime: isoDateTime,
  price: z.number().nonnegative().default(0),
  seats: z.number().int().nonnegative().default(0),
  /** Recomputed by an admin trigger when inscriptions confirm. */
  seatsBooked: z.number().int().nonnegative().default(0),
  /** Speakers — references the per-event conferenciers registry. */
  conferencierIds: z.array(z.string()).default([]),
  /** @deprecated Replaced by conferencierIds. Kept for back-compat reads. */
  intervenants: z.array(z.string()).default([]),
  /** Poster / affiche image. */
  afficheUrl: z.string().url().nullable().default(null),
  /** Detailed PDF programme. */
  programmePdfUrl: z.string().url().nullable().default(null),
});
export type EvenementAtelier = z.infer<typeof evenementAtelierSchema>;

/** Masterclass — paid like an atelier but explicitly labelled.
 *  Shares timeSlots with ateliers (a slot holds either one or the other). */
export const evenementMasterclassSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  timeSlotId: z.string().nullable().default(null),
  startDateTime: isoDateTime,
  endDateTime: isoDateTime,
  price: z.number().nonnegative().default(0),
  seats: z.number().int().nonnegative().default(0),
  seatsBooked: z.number().int().nonnegative().default(0),
  conferencierIds: z.array(z.string()).default([]),
  afficheUrl: z.string().url().nullable().default(null),
  programmePdfUrl: z.string().url().nullable().default(null),
});
export type EvenementMasterclass = z.infer<typeof evenementMasterclassSchema>;

/**
 * Audience tier — drives pack visibility and analytics. `all` means a pack is
 * available to everyone. Other values restrict the pack to the matching
 * declared status.
 */
export const evenementAudienceSchema = z.enum([
  'all',
  'etudiant',
  'jeune-diplome',
  'praticien',
]);
export type EvenementAudience = z.infer<typeof evenementAudienceSchema>;

/**
 * A bundled deal. Admin defines `includes` (a fixed set of items) and a custom
 * price (usually a discount vs the auto-sum). On the public form, picking a
 * pack auto-fills its items + overrides the line items' subtotal to `price`.
 * Extra individual items can be added on top (they're billed at full price).
 */
export const evenementPackSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(160),
  description: z.string().max(2000).optional(),
  audience: evenementAudienceSchema.default('all'),
  includes: z.object({
    general: z.boolean().default(true),
    atelierIds: z.array(z.string()).default([]),
    masterclassIds: z.array(z.string()).default([]),
  }),
  /** Total tarifaire du pack (en MAD). Affiché à côté du prix auto-somme. */
  price: z.number().nonnegative().default(0),
  /** Percentage discount applied to `price` for visitors whose declared status
   *  is in `discountAudiences` (e.g. 10% off for students / young graduates).
   *  0 = no discount. */
  discountPercent: z.number().int().min(0).max(100).default(0),
  /** Audiences that get `discountPercent` off this pack's price. */
  discountAudiences: z.array(evenementAudienceSchema).default([]),
});
export type EvenementPack = z.infer<typeof evenementPackSchema>;

/** Configuration block populated when `registrationMode === 'hosted-mtc'`. */
export const evenementHostedRegistrationSchema = z.object({
  generalInscription: z.object({
    /** When true, registering for any atelier requires the general inscription. */
    required: z.boolean().default(true),
    price: z.number().nonnegative().default(0),
    seats: z.number().int().nonnegative().default(0),
    seatsBooked: z.number().int().nonnegative().default(0),
  }),
  timeSlots: z.array(evenementTimeSlotSchema).default([]),
  /** Per-event speaker registry. Referenced by IDs from items below. */
  conferenciers: z.array(evenementConferencierSchema).default([]),
  /** Lectures included in the general inscription (not paid separately). */
  conferences: z.array(evenementConferenceSchema).default([]),
  ateliers: z.array(evenementAtelierSchema).default([]),
  masterclasses: z.array(evenementMasterclassSchema).default([]),
  /** Bundled pricing offers (e.g. "Pack Étudiant"). */
  packs: z.array(evenementPackSchema).default([]),
});
export type EvenementHostedRegistration = z.infer<typeof evenementHostedRegistrationSchema>;

export const evenementInputSchema = z.object({
  slug: slugSchema,
  title: z.string().min(2),
  subtitle: z.string().max(280).optional(),
  shortDescription: z.string().max(280),
  description: z.string(),
  category: evenementCategorySchema,
  /** Free-form thematic tags, e.g. "Implantologie", "Esthétique". */
  topics: z.array(z.string()).default([]),
  startDate: isoDateTime,
  endDate: isoDateTime,
  city: z.string().min(1),
  venue: z.string().optional(),
  country: z.string().default('Maroc'),
  organizer: z.string().min(1),
  /** Partner organisation name when the event is co-hosted with a MTC partner
   *  (used for the co-branded email and the home Vedette block badge). */
  partnerName: z.string().max(160).optional(),
  organizerWebsite: optionalUrl(),
  /** Logos of the responsible organisation(s). When set, these REPLACE the MTC
   *  logo in the header on the event detail + inscription pages (organisers
   *  want their own brand front). Empty → MTC logo shown by default. */
  organizerLogos: z.array(z.string().url()).default([]),
  programUrl: optionalUrl(),
  registrationUrl: optionalUrl(),
  registrationMode: evenementRegistrationModeSchema.default('external-link'),
  hostedRegistration: evenementHostedRegistrationSchema.optional(),
  /** Free text — "Gratuit", "Sur invitation", "200€", etc. */
  registrationInfo: z.string().max(200).optional(),
  image: z.string().url().nullable().default(null),
  status: evenementStatusSchema,
  featured: z.boolean().default(false),
  displayOrder: z.number().int().default(0),
});
export type EvenementInput = z.infer<typeof evenementInputSchema>;

export const evenementSchema = evenementInputSchema.extend({
  id: z.string(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type Evenement = z.infer<typeof evenementSchema>;
